/* ==========================================================================
   SKIN BY RAINA — native booking calendar
   --------------------------------------------------------------------------
   No scheduling app. No subscription.

   Raina's time is the scarce resource, so there is ONE calendar: the
   "Appointment Time" hold products, whose variants are 30 minute blocks with
   inventory 1. A service is a separate, untracked product carrying the price.
   Reserving adds the service line plus every consecutive hold its length
   needs, so an 80 minute Éveil takes three blocks and nothing else can be
   sold into that time.

   Shopify's oversell protection is the booking lock. Two clients cannot buy
   the same block for the same reason they cannot buy the same last sweater.

   Money: the Shopify variant price is whatever checkout actually charges,
   which is currently ZERO — booking reserves a time, it does not sell one. The
   real price rides along in the `custom.full_price` variant metafield for
   display, and is settled in person. Put a number back in the variant price and
   the same arithmetic turns it into a deposit, no code change.
   ========================================================================== */
(function () {
  'use strict';

  var MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
  var DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  var DOW = ['S','M','T','W','T','F','S'];

  /* Parse "2026-09-08T10:00" by hand. Never hand a bare string to the Date
     constructor and hope the browser agrees about timezone. */
  function parseSlot(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(s || '').trim());
    if (!m) return null;
    return {
      y: +m[1], mo: +m[2], d: +m[3], hh: +m[4], mm: +m[5],
      key: m[1] + '-' + m[2] + '-' + m[3],
      raw: m[0],
      date: new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0, 0)
    };
  }

  function fmtTime(p) {
    var h = p.hh % 12; if (h === 0) h = 12;
    return h + ':' + String(p.mm).padStart(2, '0') + (p.hh < 12 ? 'am' : 'pm');
  }
  function fmtLong(p) { return DAYS[p.date.getDay()] + ', ' + MONTHS[p.mo - 1] + ' ' + p.d; }
  function fmtShort(p) {
    return DAYS[p.date.getDay()].slice(0, 3) + ' ' + MONTHS[p.mo - 1].slice(0, 3) + ' ' + p.d;
  }
  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2
    });
  }
  function stampOf(dt) {
    return dt.getFullYear() + '-' +
      String(dt.getMonth() + 1).padStart(2, '0') + '-' +
      String(dt.getDate()).padStart(2, '0') + 'T' +
      String(dt.getHours()).padStart(2, '0') + ':' +
      String(dt.getMinutes()).padStart(2, '0');
  }

  function Booking(root) {
    var raw = root.querySelector('[data-booking-json]');
    if (!raw) return;

    var data;
    try { data = JSON.parse(raw.textContent); } catch (e) { return; }

    var els = {
      month:     root.querySelector('[data-cal-month]'),
      grid:      root.querySelector('[data-cal-grid]'),
      prev:      root.querySelector('[data-cal-prev]'),
      next:      root.querySelector('[data-cal-next]'),
      slots:     root.querySelector('[data-slots]'),
      slotsIn:   root.querySelector('[data-slots-grid]'),
      slotsDay:  root.querySelector('[data-slots-day]'),
      chooser:   root.querySelector('[data-chooser]'),
      durations: root.querySelector('[data-durations]'),
      durLabel:  root.querySelector('[data-durations-label]'),
      addon:     root.querySelector('[data-addon]'),
      addonNote: root.querySelector('[data-addon-note]'),
      title:     root.querySelector('[data-service-title]'),
      sub:       root.querySelector('[data-service-sub]'),
      tagline:   root.querySelector('[data-service-tagline]'),
      meta:      root.querySelector('[data-service-meta]'),
      desc:      root.querySelector('[data-service-desc]'),
      vWhen:     root.querySelector('[data-v-when]'),
      vSvc:      root.querySelector('[data-v-service]'),
      vDur:      root.querySelector('[data-v-duration]'),
      vTotal:    root.querySelector('[data-v-total]'),
      vOffRow:   root.querySelector('[data-v-off-row]'),
      vOffLabel: root.querySelector('[data-v-off-label]'),
      vOff:      root.querySelector('[data-v-off]'),
      vDue:      root.querySelector('[data-v-due]'),
      vBalance:  root.querySelector('[data-v-balance]'),
      submit:    root.querySelector('[data-book-submit]'),
      err:       root.querySelector('[data-book-error]')
    };

    var gran = data.granularity || 30;
    var redirect = root.getAttribute('data-book-redirect') || '/cart';
    var step = gran * 60 * 1000;

    var services = (data.services || []).filter(function (s) {
      return s && s.variants && s.variants.length;
    });
    if (!services.length) {
      if (els.grid) {
        els.grid.innerHTML = '<p class="note" style="grid-column:1/-1;padding:2rem 0">' +
          'This is not bookable online yet. Text (323)&nbsp;610-3030.</p>';
      }
      if (els.submit) els.submit.setAttribute('aria-disabled', 'true');
      return;
    }

    /* -- Index every free block by its exact minute. Fixed for the session. */
    var lead = new Date(Date.now() + (data.leadHours || 24) * 3600 * 1000);
    var byTime = Object.create(null);

    (data.slots || []).forEach(function (v) {
      if (!v.available) return;             // already sold
      var p = parseSlot(v.slot);
      if (!p) return;
      byTime[p.date.getTime()] = v.id;
    });

    /* -- State ------------------------------------------------------------ */
    var service = services[0];
    var current = service.variants[0];
    var wantsAddon = false;
    var byDay = {};
    var openDays = [];
    var view = null;
    var minView = 0, maxView = 0;
    var chosen = null;

    function idx(y, mo) { return y * 12 + mo; }
    function blocksFor(v) { return Math.max(1, Math.ceil((v.minutes || 60) / gran)); }

    /* A start time works only if every consecutive block it needs is free. */
    function computeDays() {
      var need = blocksFor(current);
      var out = Object.create(null);

      Object.keys(byTime).forEach(function (tsStr) {
        var ts = +tsStr;
        var start = new Date(ts);
        if (start <= lead) return;

        var ids = [];
        for (var k = 0; k < need; k++) {
          var id = byTime[ts + k * step];
          if (!id) return;                  // gap, this length will not fit here
          ids.push(id);
        }
        var p = parseSlot(stampOf(start));
        (out[p.key] || (out[p.key] = [])).push({ p: p, ids: ids });
      });

      Object.keys(out).forEach(function (k) {
        out[k].sort(function (a, b) { return a.p.date - b.p.date; });
      });
      return out;
    }

    function reindex() {
      byDay = computeDays();
      openDays = Object.keys(byDay).sort();
      if (!openDays.length) { view = null; return false; }

      var first = byDay[openDays[0]][0].p;
      var last = byDay[openDays[openDays.length - 1]][0].p;
      minView = idx(first.y, first.mo);
      maxView = idx(last.y, last.mo);
      if (!view || idx(view.y, view.mo) < minView || idx(view.y, view.mo) > maxView) {
        view = { y: first.y, mo: first.mo };
      }
      return true;
    }

    /* -- Render ------------------------------------------------------------ */
    function renderEmpty(msg) {
      els.grid.innerHTML = '<p class="note" style="grid-column:1/-1;padding:2rem 0">' + msg + '</p>';
      if (els.month) els.month.textContent = '';
      if (els.prev) els.prev.disabled = true;
      if (els.next) els.next.disabled = true;
      if (els.slots) els.slots.hidden = true;
      if (els.submit) els.submit.setAttribute('aria-disabled', 'true');
    }

    function renderMonth() {
      if (!view) {
        return renderEmpty('Nothing long enough is open in this window. Try a shorter length, or text (323)&nbsp;610-3030.');
      }

      if (els.month) els.month.textContent = MONTHS[view.mo - 1] + ' ' + view.y;
      if (els.prev) els.prev.disabled = idx(view.y, view.mo) <= minView;
      if (els.next) els.next.disabled = idx(view.y, view.mo) >= maxView;

      var frag = document.createDocumentFragment();

      DOW.forEach(function (d) {
        var c = document.createElement('div');
        c.className = 'cal__dow';
        c.setAttribute('aria-hidden', 'true');
        c.textContent = d;
        frag.appendChild(c);
      });

      var firstDow = new Date(view.y, view.mo - 1, 1).getDay();
      var days = new Date(view.y, view.mo, 0).getDate();

      for (var i = 0; i < firstDow; i++) {
        var pad = document.createElement('div');
        pad.className = 'cal__day';
        pad.setAttribute('data-empty', 'true');
        frag.appendChild(pad);
      }

      for (var d = 1; d <= days; d++) {
        var key = view.y + '-' + String(view.mo).padStart(2, '0') + '-' + String(d).padStart(2, '0');
        var open = !!byDay[key];
        var cell = document.createElement(open ? 'button' : 'div');
        cell.className = 'cal__day';
        cell.textContent = d;
        if (open) {
          cell.type = 'button';
          cell.setAttribute('data-open', 'true');
          cell.setAttribute('data-key', key);
          cell.setAttribute('aria-label',
            byDay[key].length + ' opening' + (byDay[key].length === 1 ? '' : 's') +
            ' on ' + MONTHS[view.mo - 1] + ' ' + d);
          if (chosen && chosen.p.key === key) cell.setAttribute('aria-selected', 'true');
        }
        frag.appendChild(cell);
      }

      els.grid.innerHTML = '';
      els.grid.appendChild(frag);
    }

    function renderSlots(key) {
      var list = byDay[key];
      if (!list || !list.length) { els.slots.hidden = true; return; }

      els.slots.hidden = false;
      if (els.slotsDay) els.slotsDay.textContent = fmtLong(list[0].p);
      els.slotsIn.innerHTML = '';

      list.forEach(function (item) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'slot';
        b.textContent = fmtTime(item.p);
        b.setAttribute('aria-pressed', chosen && chosen.p.raw === item.p.raw ? 'true' : 'false');
        b.addEventListener('click', function () { choose(item); });
        els.slotsIn.appendChild(b);
      });
    }

    function renderChooser() {
      if (!els.chooser || services.length < 2) return;
      els.chooser.innerHTML = '';

      services.forEach(function (s) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'chooser__card';
        b.setAttribute('aria-pressed', s.handle === service.handle ? 'true' : 'false');
        b.innerHTML = '<span class="chooser__s">' + s.subtitle + '</span>' +
                      '<span class="chooser__n">' + s.title + '</span>' +
                      '<span class="chooser__m">' + s.meta + '</span>';
        b.addEventListener('click', function () {
          if (s.handle === service.handle) return;
          service = s;
          current = s.variants[0];
          wantsAddon = false;
          resetSelection();
          reindex();
          renderChooser();
          renderService();
          renderDurations();
          renderAddon();
          renderSummary();
          renderMonth();
        });
        els.chooser.appendChild(b);
      });
    }

    function renderService() {
      if (els.title) els.title.textContent = service.title;
      if (els.sub) els.sub.textContent = service.subtitle;
      if (els.tagline) els.tagline.textContent = service.tagline || '';
      if (els.meta) els.meta.textContent = service.meta || '';
      if (els.desc) els.desc.textContent = service.description || '';
    }

    function renderDurations() {
      if (!els.durations) return;
      var many = service.variants.length > 1;
      if (els.durLabel) els.durLabel.hidden = !many;
      els.durations.hidden = !many;
      els.durations.innerHTML = '';
      if (!many) return;

      service.variants.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'duration';
        b.setAttribute('aria-pressed', v.id === current.id ? 'true' : 'false');
        b.innerHTML = '<span class="duration__t">' + v.label + '</span>' +
                      '<span class="duration__p">' + money(v.price) + '</span>';
        b.addEventListener('click', function () {
          if (v.id === current.id) return;
          current = v;
          resetSelection();
          reindex();
          renderDurations();
          renderSummary();
          renderMonth();
        });
        els.durations.appendChild(b);
      });
    }

    function renderAddon() {
      // Nothing is a paid add-on any more: the Nécessaire comes free with the
      // Éveil, and booked alone it IS the service. The note says so, because
      // showing nothing where the add-on used to sit reads like an omission.
      if (els.addonNote) {
        els.addonNote.hidden = !!service.addon || !service.addonNote;
        if (!service.addon && service.addonNote) els.addonNote.innerHTML = service.addonNote;
      }
      if (!els.addon) return;
      var a = service.addon;
      els.addon.hidden = !a;
      if (!a) return;

      els.addon.setAttribute('aria-pressed', wantsAddon ? 'true' : 'false');
      els.addon.innerHTML =
        '<span class="addon__box" aria-hidden="true">' + (wantsAddon ? '✓' : '') + '</span>' +
        '<span><span class="addon__t">Add ' + a.title +
        ' <span class="addon__p">' + money(a.price) + '</span></span>' +
        '<span class="addon__d">' + a.description + '</span></span>';
    }

    function renderSummary() {
      var addonCents = (wantsAddon && service.addon) ? service.addon.price : 0;
      var gross = current.price + addonCents;

      // Shopify applies an automatic discount across the whole cart, so it
      // comes off the deposit line too. Model it the same way here or the
      // number on the button will not match the number at checkout.
      var rate = data.discountRate || 0;
      var off = Math.round(gross * rate);
      var total = gross - off;
      var due = Math.round((current.deposit + addonCents) * (1 - rate));

      if (els.vSvc) {
        els.vSvc.textContent = service.title +
          (wantsAddon && service.addon ? ' + ' + service.addon.title : '');
      }
      if (els.vDur) els.vDur.textContent = current.minutes + ' minutes';
      if (els.vTotal) els.vTotal.textContent = money(gross);
      if (els.vOffRow) els.vOffRow.hidden = !off;
      if (els.vOffLabel) els.vOffLabel.textContent = Math.round(rate * 100) + '% off';
      if (els.vOff) els.vOff.textContent = '\u2212' + money(off);
      // "$0" invites a double take. Say what is true: nothing is charged today.
      if (els.vDue) els.vDue.textContent = due ? money(due) : 'Nothing';
      if (els.vBalance) els.vBalance.textContent = money(total - due);
    }

    function resetSelection() {
      chosen = null;
      if (els.vWhen) els.vWhen.textContent = 'Not chosen yet';
      if (els.submit) els.submit.setAttribute('aria-disabled', 'true');
      if (els.err) els.err.hidden = true;
      if (els.slots) els.slots.hidden = true;
    }

    function choose(item) {
      chosen = item;
      renderMonth();
      renderSlots(item.p.key);
      if (els.vWhen) els.vWhen.textContent = fmtShort(item.p) + ' · ' + fmtTime(item.p);
      if (els.submit) els.submit.removeAttribute('aria-disabled');
      if (els.err) els.err.hidden = true;
    }

    /* -- Wire --------------------------------------------------------------- */
    els.grid.addEventListener('click', function (e) {
      var cell = e.target.closest('[data-open="true"]');
      if (!cell) return;
      chosen = null;
      if (els.vWhen) els.vWhen.textContent = 'Not chosen yet';
      if (els.submit) els.submit.setAttribute('aria-disabled', 'true');
      renderMonth();
      renderSlots(cell.getAttribute('data-key'));
      els.slots.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });

    if (els.addon) els.addon.addEventListener('click', function () {
      wantsAddon = !wantsAddon;
      renderAddon();
      renderSummary();
    });

    if (els.prev) els.prev.addEventListener('click', function () {
      if (--view.mo < 1) { view.mo = 12; view.y--; }
      renderMonth();
    });
    if (els.next) els.next.addEventListener('click', function () {
      if (++view.mo > 12) { view.mo = 1; view.y++; }
      renderMonth();
    });

    function dropChosen() {
      if (!chosen) return;
      var key = chosen.p.key;
      byDay[key] = (byDay[key] || []).filter(function (x) { return x.p.raw !== chosen.p.raw; });
      if (!byDay[key].length) delete byDay[key];
      chosen = null;
      renderMonth();
      if (byDay[key]) renderSlots(key); else els.slots.hidden = true;
    }

    if (els.submit) els.submit.addEventListener('click', function () {
      if (!chosen || els.submit.getAttribute('aria-disabled') === 'true') return;

      els.submit.setAttribute('aria-disabled', 'true');
      var label = els.submit.textContent;
      els.submit.textContent = 'Holding your time';

      var when = fmtLong(chosen.p) + ' at ' + fmtTime(chosen.p);

      // The order total is $0, so the order itself cannot say what this costs.
      // These properties can: they show in the cart, on the order in admin, and
      // in Shopify's own confirmation email, which is what makes that email
      // carry the next step without a template edit.
      var rate = data.discountRate || 0;
      var dueAtVisit = current.price - Math.round(current.price * rate) -
                       Math.round(current.deposit * (1 - rate));

      var items = [{
        id: current.id,
        quantity: 1,
        properties: {
          'Appointment': when,
          'Length': current.minutes + ' minutes',
          'Due at your appointment': money(dueAtVisit),
          'Next step': 'Complete your intake form in your portal — skinbyraina.com/pages/portal',
          // Underscore prefixed properties stay out of cart and order display
          // but remain readable in Liquid. This is what the client portal
          // sorts on and builds calendar files from.
          '_slot': chosen.p.raw,
          '_minutes': String(current.minutes || 60)
        }
      }];

      if (wantsAddon && service.addon) {
        items.push({
          id: service.addon.id,
          quantity: 1,
          properties: { 'For': when }
        });
      }

      chosen.ids.forEach(function (id) {
        items.push({ id: id, quantity: 1, properties: { '_hold': '1' } });
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.j.description || res.j.message || 'That time was just taken.');
          if (redirect !== '#') window.location.href = redirect;
        })
        .catch(function (err) {
          els.submit.textContent = label;
          els.submit.removeAttribute('aria-disabled');
          if (els.err) {
            els.err.hidden = false;
            els.err.textContent = err.message + ' Please pick another opening.';
          }
          dropChosen();
        });
    });

    /* -- Go ----------------------------------------------------------------- */
    renderChooser();
    renderService();
    renderDurations();
    renderAddon();
    resetSelection();
    renderSummary();
    reindex();
    if (!openDays.length && !Object.keys(byTime).length) {
      return renderEmpty('No openings are published right now. Text (323)&nbsp;610-3030 and Raina will find you a time.');
    }
    renderMonth();
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-booking]'), Booking);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
